import React from "react"
import { useStaticQuery, graphql } from "gatsby"
import { useTranslation } from "gatsby-plugin-react-i18next"
import makeAnimated from "react-select/animated"
import Select from "react-select"
import dayjs from "dayjs"
import isSameOrAfter from "dayjs/plugin/isSameOrAfter"
import isSameOrBefore from "dayjs/plugin/isSameOrBefore"
import isBetween from "dayjs/plugin/isBetween"
import customParseFormat from "dayjs/plugin/customParseFormat"
import ContentTabs from "../atoms/content/ContentTabs"
import ContentTab from "../atoms/content/ContentTab"
import Time from "../atoms/schedule/Time"
import Schedule from "./Schedule"
import classes from "./Program.module.scss"
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
dayjs.extend(isBetween)
dayjs.extend(customParseFormat)

const animatedComponents = makeAnimated()

const ContentPageTemplate: React.FC = () => {
  const { i18n, t } = useTranslation("program")

  const data = useStaticQuery<GatsbyTypes.ScheduleQuery>(graphql`
    query Schedule {
      allScheduleJaCsv {
        nodes {
          id
          date
          start
          end
          place
          name
          genre
          description
          link
        }
      }
      allScheduleEnCsv {
        nodes {
          id
          date
          start
          end
          place
          name
          genre
          description
          link
        }
      }
      images: allFile(
        filter: { relativeDirectory: { regex: "$/schedules/" } }
      ) {
        edges {
          node {
            relativePath
            name
            childImageSharp {
              fluid(maxHeight: 300) {
                sizes
                base64
                aspectRatio
                src
                srcSet
              }
            }
          }
        }
      }
    }
  `)
  let scheduleData =
    i18n.language === "en"
      ? data.allScheduleEnCsv.nodes
      : data.allScheduleJaCsv.nodes

  const genres = Array.from(
    new Set(
      scheduleData.flatMap(n => n.genre?.split(",")).filter(Boolean)
    ).values()
  ).map(v => ({ value: v, label: v }))
  const places = Array.from(
    new Set(scheduleData.map(node => node.place).filter(Boolean)).values()
  ).map(v => ({ value: v, label: v }))

  const [selectedGenre, setSelectedGenre] = React.useState<typeof genres>([])
  const [selectedPlace, setSelectedPlace] = React.useState<typeof places>([])
  const [startTime, setStartTime] = React.useState<string>("")
  const [endTime, setEndTime] = React.useState<string>("")
  const selectedGenreValue = selectedGenre.map(({ value }) => value)
  const selectedPlaceValue = selectedPlace.map(({ value }) => value)

  const hasIncludeGenre = (
    genre: GatsbyTypes.Maybe<string>,
    selectedGenreValue: string[]
  ) => {
    for (const value of selectedGenreValue) {
      if (genre !== undefined && genre.includes(value)) {
        return true
      }
    }
  }
  scheduleData = scheduleData.filter(({ genre, place, start, end }) => {
    if (
      selectedGenre.length > 0 &&
      !selectedGenreValue.includes(genre || "") &&
      !hasIncludeGenre(genre, selectedGenreValue)
    ) {
      return false
    }
    if (selectedPlace.length > 0 && !selectedPlaceValue.includes(place || "")) {
      return false
    }
    if (
      startTime !== "" &&
      !dayjs(start, "h:mm").isSameOrAfter(dayjs(startTime, "h:mm"))
    ) {
      return false
    }
    if (
      endTime !== "" &&
      !dayjs(end, "h:mm").isSameOrBefore(dayjs(endTime, "h:mm"))
    ) {
      return false
    }
    return true
  })

  return (
    <>
      <h4>{t("time")}</h4>
      <div className={classes.TimeRow}>
        <Time value={startTime} onChange={v => setStartTime(v.target.value)} />
        <span className={classes.Hyphen}>-</span>{" "}
        <Time value={endTime} onChange={v => setEndTime(v.target.value)} />
      </div>
      <h4>{t("genre")}</h4>
      <Select
        styles={{ menu: provided => ({ ...provided, zIndex: 2 }) }}
        options={genres}
        isMulti
        defaultValue={[]}
        name="genre"
        components={animatedComponents}
        onChange={newValue => {
          setSelectedGenre((newValue as unknown) as string[])
        }}
        placeholder={t("select")}
      />
      <h4>{t("place")}</h4>
      <Select
        styles={{ menu: provided => ({ ...provided, zIndex: 2 }) }}
        options={places}
        isMulti
        defaultValue={[]}
        name="places"
        components={animatedComponents}
        onChange={newValue =>
          setSelectedPlace((newValue as unknown) as string[])
        }
        placeholder={t("select")}
      />
      <div className={classes.ProgramWrapper}>
        <ContentTabs>
          <ContentTab
            name={t("day1")}
            tabId="event_introduction"
            defaultCheckedPath={["/program", "/program/1st_day"]}
          >
            <Schedule
              nodes={scheduleData.filter(({ date }) =>
                dayjs(date, "YYYY/MM/DD").isBetween(
                  "2022-01-07",
                  "2022-01-09",
                  "day"
                )
              )}
              images={data.images}
            />
          </ContentTab>
          <ContentTab
            name={t("day2")}
            tabId="event_introduction"
            defaultCheckedPath={["/program/2nd_day"]}
          >
            <Schedule
              nodes={scheduleData.filter(({ date }) =>
                dayjs(date, "YYYY/MM/DD").isBetween(
                  "2022-01-08",
                  "2022-01-10",
                  "day"
                )
              )}
              images={data.images}
            />
          </ContentTab>
        </ContentTabs>
      </div>
    </>
  )
}

export default ContentPageTemplate
